import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, CheckCircle, ChevronRight, ChevronDown,
  Menu, X, MessageCircle, Target,
  Users, ClipboardList, Sparkles, Award,
  Shield, FileText, Download, Gift, Plane,
  Rocket, Trophy, DollarSign, CreditCard,
  Globe, Send, Briefcase, Tag, Calendar, Image, Video, ShoppingBag,
  Star, Quote, BadgeCheck, Share2, Tv, Radio, Monitor, Link as LinkIcon, Mail, Film,
  Zap, Building2, Volume2, VolumeX, BarChart3, TrendingUp, Bot, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { Logo, LogoIcon } from '../components/Logo';
import { 
  WaveDivider, 
  FloatingParticles, 
  GridPattern, 
  GlowLine,
  GradientOrb 
} from '../components/BackgroundEffects';

// External Directory URL
const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

// External Pricing URL
const EXTERNAL_PRICING_URL = 'https://www.aihpower.com/pricing';

// Directory Logo
const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

const FEATURES = [
  {
    icon: Target,
    title: 'Done-For-You (DFY)',
    description: 'Submit your project brief and our virtual team handles everything. 40+ departments at your service including websites, funnels, AI agents, and more.'
  },
  {
    icon: Users,
    title: 'Done-With-You (DWY)',
    description: 'Book a Zoom session with our experts. Get hands-on guidance, real-time collaboration, and learn as you build.'
  },
  {
    icon: MessageCircle,
    title: 'Do-It-Yourself (DIY)',
    description: 'Access over 3000+ AI tools, CRM knowledgebase, and AI support. Create professional content yourself with our comprehensive AI Studio.'
  }
];

const STEPS = [
  { step: 1, title: 'Choose Your Service', description: 'Browse 40+ departments and services' },
  { step: 2, title: 'Build Your Project Brief', description: 'AI-guided intake in 60 seconds' },
  { step: 3, title: 'Track Progress', description: 'Real-time updates from submission to completion' },
  { step: 4, title: 'Review & Approve', description: 'Price-on-demand proposals with transparent pricing' }
];

// Success Journey Steps for Landing Page
const SUCCESS_JOURNEY = [
  { step: 1, title: 'Start Free Trial', description: '30 days free, full access to everything', icon: CheckCircle },
  { step: 2, title: 'Explore & Learn', description: '40+ departments, 30+ AI tools at your fingertips', icon: Sparkles },
  { step: 3, title: 'Get Co-Op Funding', description: '$120k credits (4% Fee or 5% Easy Pay)', icon: Gift },
  { step: 4, title: 'Get AI Certified', description: 'Earn "Voted Best of Best" badge', icon: Award },
  { step: 5, title: 'Scale Your Business', description: 'Done-For-You services on demand', icon: Rocket },
  { step: 6, title: 'Achieve Success!', description: 'Log in daily to your Workspace', icon: Trophy },
];

const DEPARTMENTS_PREVIEW = [
  { name: 'Websites & Landing Pages', count: 8 },
  { name: 'Funnels & Lead Capture', count: 6 },
  { name: 'AI Agents & Voice', count: 5 },
  { name: 'Email Marketing', count: 4 },
  { name: 'Paid Ads', count: 6 },
  { name: 'Automations', count: 5 },
];

// Trust Elements Data
const TRUSTED_LOGOS = [
  { name: 'GoHighLevel', logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=200&h=60&fit=crop' },
  { name: 'Stripe', logo: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=200&h=60&fit=crop' },
  { name: 'Zapier', logo: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=200&h=60&fit=crop' },
  { name: 'HubSpot', logo: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=200&h=60&fit=crop' },
  { name: 'Salesforce', logo: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=200&h=60&fit=crop' },
  { name: 'Mailchimp', logo: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=200&h=60&fit=crop' },
];

const TESTIMONIALS = [
  {
    name: 'Alison Garcia',
    role: 'Mortgage Lender',
    company: 'Providence Home Loans',
    image: 'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/cplryjux_alison%20garcia.jpeg',
    rating: 5,
    quote: "The team delivered our entire marketing funnel in 5 days. What would have taken us months, they did in less than a week. Absolutely incredible service!",
    country: 'United States'
  },
  {
    name: 'Dennis Hubbard',
    role: 'Founder & CEO',
    company: 'Credit Firm Inc',
    image: 'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/qt0bq8i5_dennis%20hubbard.jpeg',
    rating: 5,
    quote: "I've tried several agencies before, but nothing compares to the AI Horse-Powered team. The Co-Op Funds alone saved us over $50,000 in our first year.",
    country: 'United States'
  },
  {
    name: "Jim O'Farrell",
    role: 'Owner',
    company: 'JNO Roofing',
    image: 'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/am034sgs_jimmy%20ofarrell.png',
    rating: 5,
    quote: "From AI chatbots to full CRM setup, they handled everything. Our lead conversion increased by 340% in the first 90 days. Best investment we ever made.",
    country: 'United States'
  },
  {
    name: 'Dennis Voice',
    role: 'HVAC Sales Manager',
    company: 'Voice Heating & Cooling',
    image: 'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/jiyo02lp_dennis%20voice.png',
    rating: 5,
    quote: "As a local service business, I didn't think I could afford this level of marketing. The Co-Op program made it possible. Now I'm booked out 3 months in advance!",
    country: 'United States'
  },
  {
    name: 'Lisa Patel',
    role: 'Digital Marketing Consultant',
    company: 'Patel Consulting',
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
    rating: 5,
    quote: "I recommend AI Horse-Powered to all my clients. The quality of work is exceptional, and the turnaround time is unmatched. A true game-changer for any business.",
    country: 'United Kingdom'
  },
  {
    name: 'David Kim',
    role: 'E-commerce Entrepreneur',
    company: 'Luxe Lifestyle',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
    rating: 5,
    quote: "The AI automation they built for us handles 80% of our customer inquiries automatically. Our support team can now focus on high-value conversations.",
    country: 'Singapore'
  },
];

const PLATFORM_RATINGS = [
  { platform: 'Google', rating: 4.9, reviews: 847, color: 'from-blue-500 to-blue-600' },
  { platform: 'Trustpilot', rating: 4.8, reviews: 1203, color: 'from-green-500 to-green-600' },
  { platform: 'Facebook', rating: 5.0, reviews: 634, color: 'from-indigo-500 to-indigo-600' },
];

const SOCIAL_PROOF_AVATARS = [
  'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/cplryjux_alison%20garcia.jpeg',
  'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/qt0bq8i5_dennis%20hubbard.jpeg',
  'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/am034sgs_jimmy%20ofarrell.png',
  'https://customer-assets.emergentagent.com/job_socialmarketdir/artifacts/jiyo02lp_dennis%20voice.png',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=50&h=50&fit=crop&crop=face',
];

// Star Rating Component
const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClasses = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : 'w-6 h-6';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star} 
          className={`${sizeClasses} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} 
        />
      ))}
    </div>
  );
};

// Rotating Systems Text Component
const RotatingPODText = () => {
  const words = ['Workspace', 'AGI Workhorse', 'CRM', 'POD', 'Directory', 'Ad Manager', 'Agents'];
  const colors = ['text-primary', 'text-yellow-400', 'text-cyan-400', 'text-purple-400', 'text-green-400', 'text-blue-400', 'text-pink-400'];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <p className="text-gray-400 mb-8 h-8 flex items-center justify-center gap-2">
      <span className="text-gray-400">AI-Powered</span>
      <span 
        className={`inline-block font-semibold transition-all duration-300 ${colors[currentIndex]} ${
          isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
        }`}
      >
        {words[currentIndex]}
      </span>
    </p>
  );
};

// Trusted By Logo Bar Component  
const TrustedByBar = () => (
  <div className="py-8 bg-black/40 backdrop-blur-sm border-y border-white/5">
    <div className="max-w-7xl mx-auto px-6">
      <p className="text-center text-sm text-gray-500 uppercase tracking-wider mb-6">
        Trusted by businesses worldwide
      </p>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-60">
        {TRUSTED_LOGOS.map((logo, i) => (
          <div key={i} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center">
              <BadgeCheck className="w-5 h-5" />
            </div>
            <span className="text-sm font-medium">{logo.name}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Social Proof Counter Component
const SocialProofCounter = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="flex items-center justify-center gap-3 py-4"
  >
    <div className="flex -space-x-3">
      {SOCIAL_PROOF_AVATARS.map((avatar, i) => (
        <img 
          key={i}
          src={avatar}
          alt=""
          className="w-10 h-10 rounded-full border-2 border-background object-cover"
        />
      ))}
    </div>
    <div className="text-left">
      <p className="text-white font-semibold">20,000+ Businesses</p>
      <p className="text-sm text-gray-400">trust AI Horse-Powered</p>
    </div>
  </motion.div>
);

// Testimonial Card Component
const TestimonialCard = ({ testimonial, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="glass rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all h-full flex flex-col"
  >
    <div className="flex items-start gap-4 mb-4">
      <img 
        src={testimonial.image} 
        alt={testimonial.name}
        className="w-14 h-14 rounded-full object-cover border-2 border-primary/30"
      />
      <div className="flex-1">
        <h4 className="font-semibold text-white">{testimonial.name}</h4>
        <p className="text-sm text-gray-400">{testimonial.role}</p>
        <p className="text-xs text-primary">{testimonial.company}</p>
      </div>
    </div>
    <StarRating rating={testimonial.rating} />
    <p className="text-gray-300 mt-4 flex-1 text-sm leading-relaxed">
      "{testimonial.quote}"
    </p>
    <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
      <Globe className="w-3 h-3" /> {testimonial.country}
    </p>
  </motion.div>
);

const ChatDemo = () => {
  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const demoConversation = [
    { type: 'ai', text: "Hello! I'm your AI Concierge. What project can I help you with today?" },
    { type: 'user', text: "I need a landing page for my coaching business" },
    { type: 'ai', text: "Perfect! Let me help you create a project brief. What's your primary goal - lead capture, bookings, or sales?" },
    { type: 'user', text: "Booking calls with potential clients" },
    { type: 'ai', text: "Got it! I'll set up a high-converting landing page with calendar integration. Estimated delivery: 5 days. Ready to get started?" }
  ];
  
  useEffect(() => {
    if (currentIndex < demoConversation.length) {
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, demoConversation[currentIndex]]);
        setCurrentIndex(prev => prev + 1);
      }, currentIndex === 0 ? 1000 : 2000);
      return () => clearTimeout(timer);
    } else {
      const resetTimer = setTimeout(() => {
        setMessages([]);
        setCurrentIndex(0);
      }, 5000);
      return () => clearTimeout(resetTimer);
    }
  }, [currentIndex]);
  
  return (
    <div className="glass rounded-2xl p-6 max-w-md mx-auto">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
        <LogoIcon size="md" />
        <div>
          <h4 className="font-semibold text-white">AI Concierge</h4>
          <p className="text-xs text-gray-400">Building your project brief...</p>
        </div>
      </div>
      <div className="space-y-3 h-[280px] overflow-hidden">
        {messages.map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
              msg.type === 'user' 
                ? 'bg-primary text-black' 
                : 'bg-white/5 text-gray-200'
            }`}>
              {msg.text}
            </div>
          </motion.div>
        ))}
        {currentIndex < demoConversation.length && (
          <div className="flex gap-1 px-4 py-2">
            <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await api.seedData();
      toast.success('Demo data loaded successfully!');
      console.log('Seed result:', response.data);
    } catch (error) {
      if (error.response?.data?.message?.includes('already seeded')) {
        toast.info('Data already exists');
      } else {
        toast.error('Failed to seed data');
      }
    } finally {
      setSeeding(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Gradient Overlay */}
      <div className="fixed inset-0 bg-hero-glow pointer-events-none" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" showText={true} />
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a>
              <a href="#departments" className="text-gray-400 hover:text-white transition-colors">Departments</a>
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Directory</a>
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Pricing</a>
              <a href="#support" className="text-gray-400 hover:text-white transition-colors">Support</a>
              <Link to="/login">
                <Button variant="ghost" className="text-white" data-testid="nav-login-btn">Enter</Button>
              </Link>
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary" data-testid="nav-get-started-btn">
                  Start Free Trial
                </Button>
              </a>
            </div>
            
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden py-4 space-y-4"
            >
              <a href="#how-it-works" className="block text-gray-400 hover:text-white">How It Works</a>
              <a href="#departments" className="block text-gray-400 hover:text-white">Departments</a>
              <div className="flex items-center gap-3 pt-2">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">Enter</Button>
                </Link>
                <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="btn-primary w-full">Get Started</Button>
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </nav>
      
      {/* Clean App-Like Login Section - For Chatbot Popup Context */}
      <section className="pt-24 pb-16 px-6 relative overflow-hidden min-h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-cyan-950/20" />
        <GridPattern className="opacity-10" />
        
        <div className="relative z-10 w-full max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <Logo size="lg" showText={false} />
            </div>
            
            {/* App Name */}
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
              AI Horse-Powered
            </h1>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-2">
              <span className="text-gradient">SYSTEMS</span>
            </h2>
            
            {/* Rotating Text */}
            <RotatingPODText />
            
            {/* Quick Action Buttons */}
            <div className="space-y-3 mb-6">
              <Link to="/login" className="block">
                <Button className="w-full py-6 text-lg bg-white text-black hover:bg-gray-100" data-testid="app-enter-btn">
                  Enter
                </Button>
              </Link>
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full py-6 text-lg bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="app-getlisted-btn">
                  Start Your 30-Day Free Trial
                </Button>
              </a>
            </div>
            
            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-gray-500 text-sm">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            
            {/* Learn More Link */}
            <a href="#the-problem" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <span>Learn more about AI Horse-Power</span>
              <ChevronRight className="w-4 h-4" />
            </a>
            
            {/* Trust Badge */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="flex items-center justify-center gap-4">
                <div className="flex -space-x-2">
                  {SOCIAL_PROOF_AVATARS.slice(0, 4).map((avatar, i) => (
                    <img key={i} src={avatar} alt="" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                  ))}
                </div>
                <div className="text-left">
                  <p className="text-white text-sm font-medium">20,000+ businesses</p>
                  <div className="flex items-center gap-1">
                    <StarRating rating={5} size="sm" />
                    <span className="text-gray-400 text-xs">4.9/5</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* THE PROBLEM: Frustrated Business Owner Story */}
      <section id="the-problem" className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-red-950/20 via-background to-background">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/5 via-transparent to-red-900/5" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Problem Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 mb-6">
              <X className="w-4 h-4 text-pink-400" />
              <span className="text-sm text-pink-400 font-medium">Sound Familiar?</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Buried in <span className="text-pink-400">Tools, Tech & Confusion</span>?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              You're not alone. Thousands of business owners struggle with complex CRMs, confusing ad platforms, and tech overwhelm.
            </p>
          </motion.div>

          {/* Problem Images Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* System Fails Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-pink-500/30 shadow-2xl shadow-pink-500/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/fxqhtw48_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2002_34_54%20PM.png"
                  alt="Frustrated business owner with system failures"
                  className="w-full h-[300px] object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-pink-500/30">
                <p className="text-pink-400 font-bold text-lg">System Failures?</p>
                <p className="text-gray-300 text-sm">When technology becomes your enemy instead of your ally.</p>
              </div>
            </motion.div>

            {/* Overwhelmed Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-pink-500/30 shadow-2xl shadow-pink-500/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/0069bzot_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2002_43_55%20PM.png"
                  alt="Overwhelmed business owner with too many tools"
                  className="w-full h-[300px] object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-pink-500/30">
                <p className="text-pink-400 font-bold text-lg">Too Many Tools?</p>
                <p className="text-gray-300 text-sm">Dashboards, CRMs, ad platforms... information overload.</p>
              </div>
            </motion.div>
          </div>

          {/* Transition Arrow */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex flex-col items-center">
              <p className="text-gray-500 text-lg mb-4">There's a better way...</p>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-cyan-500 flex items-center justify-center animate-bounce">
                <ChevronRight className="w-6 h-6 text-black rotate-90" />
              </div>
            </div>
          </motion.div>

          {/* Solution Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">The AI Horse-Powered Solution</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Meet Your <span className="text-gradient">AI Horse-Powered Systems</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              40+ virtual departments working for you 24/7. Expert support when you need it. Results you can see.
            </p>
          </motion.div>

          {/* Solution Images - Hero Features */}
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {/* AI Horse Power Main - VIDEO */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 relative group"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-primary/30 shadow-2xl shadow-primary/10 bg-black relative">
                <video 
                  id="hero-video"
                  src="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/qimg4w4u_Rise%20With%20Horsepower%20-%20VEED%20Export-1768962054660.mp4"
                  poster="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/yg9mrpa3_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2003_17_41%20PM.png"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  loading="lazy"
                  className="w-full aspect-video object-contain bg-black"
                  style={{ backgroundColor: '#000' }}
                />
                {/* Sound Toggle Button */}
                <button
                  onClick={(e) => {
                    const video = document.getElementById('hero-video');
                    if (video) {
                      video.muted = !video.muted;
                      e.currentTarget.querySelector('.sound-on').classList.toggle('hidden');
                      e.currentTarget.querySelector('.sound-off').classList.toggle('hidden');
                    }
                  }}
                  className="absolute bottom-4 right-4 p-3 bg-black/70 hover:bg-black/90 backdrop-blur-sm rounded-full border border-white/20 transition-all group-hover:opacity-100 opacity-70"
                  title="Toggle Sound"
                >
                  <Volume2 className="w-5 h-5 text-white sound-on hidden" />
                  <VolumeX className="w-5 h-5 text-white sound-off" />
                </button>
              </div>
            </motion.div>

            {/* Support Expert */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative group"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-cyan-500/30 shadow-2xl shadow-cyan-500/10 h-full">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/gwqcrehj_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2005_50_18%20PM.png"
                  alt="Expert support ready to help"
                  className="w-full h-full object-cover min-h-[280px]"
                />
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-cyan-500/30">
                <p className="text-cyan-400 font-bold">Expert Support</p>
                <p className="text-gray-300 text-xs">Real humans + AI, ready when you need us.</p>
              </div>
            </motion.div>
          </div>

          {/* POD Features Grid - Clean Icon-Based Design */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { name: '40+ Virtual Departments', color: 'purple', icon: Users },
                { name: 'Best of Best Directory', color: 'amber', icon: Globe },
                { name: 'CRM Operating System', color: 'blue', icon: BarChart3 },
                { name: 'Cinematic Ad System', color: 'cyan', icon: Video },
                { name: 'Affiliate Network', color: 'green', icon: Share2 },
                { name: '$120K Co-Op Funding', color: 'yellow', icon: DollarSign },
              ].map((item, i) => {
                const Icon = item.icon;
                const colorClasses = {
                  purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
                  amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400',
                  blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
                  cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400',
                  green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
                  yellow: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30 text-yellow-400',
                };
                const iconColors = {
                  purple: 'text-purple-400',
                  amber: 'text-amber-400',
                  blue: 'text-blue-400',
                  cyan: 'text-cyan-400',
                  green: 'text-green-400',
                  yellow: 'text-yellow-400',
                };
                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className={`relative group p-4 rounded-xl bg-gradient-to-br ${colorClasses[item.color]} border backdrop-blur-sm hover:scale-105 transition-all duration-300 cursor-default`}
                  >
                    <div className="flex flex-col items-center text-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-black/30 flex items-center justify-center ${iconColors[item.color]}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-sm font-medium ${iconColors[item.color]}`}>{item.name}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Success Stories - Ads Launched & Money */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Ads Launched */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-green-500/30 shadow-2xl shadow-green-500/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/d1qy7j60_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2003_10_09%20PM.png"
                  alt="Ads launched successfully"
                  className="w-full h-[280px] object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-green-500/30">
                <p className="text-green-400 font-bold text-lg">Ads Launched! ✓</p>
                <p className="text-gray-300 text-sm">From confusion to campaigns running everywhere.</p>
              </div>
            </motion.div>

            {/* Money Results */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-yellow-500/30 shadow-2xl shadow-yellow-500/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b736e951-cda2-4761-8f2b-3d5fa1c32c0d/artifacts/4r68dyed_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2003_12_30%20PM.png"
                  alt="Business growth and success"
                  className="w-full h-[280px] object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-yellow-500/30">
                <p className="text-yellow-400 font-bold text-lg">Real Revenue Growth 📈</p>
                <p className="text-gray-300 text-sm">Our members see results, not just dashboards.</p>
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-10 py-6">
                Escape the Chaos — Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* AGI Workhorse™ Section */}
      <section className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-black via-yellow-950/10 to-black">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-900/10 via-transparent to-primary/10" />
        <FloatingParticles count={12} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/30 mb-6">
                <Bot className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-yellow-400 font-medium">AGI Workhorse™</span>
              </div>
              
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Where Artificial General Intelligence <span className="text-yellow-400">Goes to Work</span>
              </h2>
              
              <p className="text-gray-400 text-lg mb-6">
                An execution-driven AGI system that performs real business tasks directly inside your CRM, Workspace, and Directory. 
                It doesn't just suggest what to do. <span className="text-yellow-400 font-semibold">It does it.</span>
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">CRM Execution</h4>
                    <p className="text-gray-400 text-sm">Manage leads, send communications, create opportunities, generate invoices, and deploy automations.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Package className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Workspace Operations</h4>
                    <p className="text-gray-400 text-sm">Create orders, launch marketing tasks, track fulfillment, and manage operational workflows.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Directory Control</h4>
                    <p className="text-gray-400 text-sm">Create listings, launch offers, route leads into CRM, and monitor performance.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-medium">Endurance</span>
                <span className="px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-medium">Precision</span>
                <span className="px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-medium">Scale</span>
                <span className="px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-medium">Structured Execution</span>
              </div>
              
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-yellow-600 hover:bg-yellow-700 text-white">
                  Deploy Your AGI Workhorse™
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
            
            {/* Right: Visual */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Workhorse UI Preview */}
              <div className="bg-[#0a0a0f] rounded-2xl border border-white/10 p-6 shadow-2xl shadow-yellow-500/10">
                {/* Header */}
                <div className="flex items-center gap-3 pb-4 border-b border-white/10 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500/30 to-primary/30 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">AGI Workhorse™</h4>
                    <p className="text-xs text-gray-500">Where AGI goes to work</p>
                  </div>
                  <div className="ml-auto flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="text-xs text-green-400">Ready</span>
                  </div>
                </div>
                
                {/* How It Works Steps */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 text-xs font-bold">1</div>
                    <span className="text-sm text-gray-300">You give a command</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 text-xs font-bold">2</div>
                    <span className="text-sm text-gray-300">Workhorse evaluates inputs & risk</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 text-xs font-bold">3</div>
                    <span className="text-sm text-gray-300">Executes inside CRM, Workspace, or Directory</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 text-xs font-bold">4</div>
                    <span className="text-sm text-gray-300">Logs and confirms the result</span>
                  </div>
                </div>
                
                {/* Security Features */}
                <div className="bg-gradient-to-br from-primary/10 to-purple-500/10 rounded-xl p-4 border border-primary/20">
                  <p className="text-xs text-primary font-medium mb-2">Built for Power. Designed for Control.</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Tenant isolation</span>
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Encrypted integrations</span>
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Risk confirmations</span>
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Kill-switch protection</span>
                  </div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-r from-yellow-500/20 via-transparent to-primary/20 blur-3xl" />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Features Section (Previously Hero Content) */}
      <section id="features" className="py-20 px-6 relative overflow-hidden">
        {/* Background Image - Desktop (1800x600) */}
        <div 
          className="absolute inset-0 hidden md:block bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_marketai-hub-17/artifacts/353hl6x6_Untitled%20%281800%20x%20600%20px%29.png')`,
            backgroundPosition: 'right center',
            backgroundSize: 'cover',
          }}
        />
        {/* Background Image - Mobile (800x1200) */}
        <div 
          className="absolute inset-0 md:hidden bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_marketai-hub-17/artifacts/6v8ofjuc_Untitled%20%28800%20x%201200%20px%29%20%282%29.png')`,
            backgroundPosition: 'center bottom',
            backgroundSize: 'cover',
          }}
        />
        {/* Dark Overlay - Strong on left for text, minimal on right to show horse */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40 md:hidden" />
        
        <FloatingParticles count={10} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Bold Headline */}
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                Build. Launch. Scale.{' '}
                <span className="text-gradient">Dominate.</span>
              </h2>
              
              {/* Subheadline */}
              <p className="text-xl text-gray-300 mb-4">
                The <strong className="text-white">AI Horse-Powered Systems&#8482;</strong>
              </p>
              
              {/* Workspace Feature Pills */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  7 Platform Systems
                </span>
                <span className="px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium">
                  AI Workforce
                </span>
                <span className="px-3 py-1.5 rounded-full bg-green-500/10 text-green-400 text-sm font-medium">
                  30 Days Free
                </span>
              </div>
              
              {/* Value Proposition */}
              <p className="text-lg text-gray-400 mb-6 max-w-xl">
                Access a complete business growth engine backed by{' '}
                <span className="text-green-400 font-semibold">$120,000 in Co-Op Funds</span>.
              </p>
              
              {/* Execute faster with: */}
              <div className="mb-6">
                <p className="text-sm text-primary font-semibold uppercase tracking-wide mb-3">Execute faster with:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'DFY, DWY & DIY services',
                    '40+ AI-powered virtual departments',
                    'Managed ads, automation & sales support',
                    'Cinematic Ad System™ & publishing',
                    'CRM + BDC-X for lead handling and follow-up',
                    '"Best of Best" marketplace visibility',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Tagline */}
              <p className="text-gray-400 italic mb-6">
                No silos. No waiting.<br />
                <span className="text-white font-medium">Just momentum — on demand.</span>
              </p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="btn-primary text-lg px-8 py-6" data-testid="hero-get-started-btn">
                    Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-black text-lg px-8 py-6" data-testid="hero-get-listed-btn">
                    View Plans <Building2 className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </div>
              
              {/* Free Trial Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-400 font-medium">30 Days Free • No Credit Card Required</span>
              </div>
            </motion.div>
            
            {/* Right side - empty to let horse background show */}
            <div className="hidden lg:block" />
          </div>
        </div>
      </section>
      
      {/* Trust Element 1: Trusted By Logo Bar */}
      <TrustedByBar />

      {/* AI Horse-Powered Systems Section - from aihpower.com */}
      <section className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-black via-cyan-950/10 to-black">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-cyan-900/5" />
        <FloatingParticles count={12} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">AI Horse-Powered Systems&#8482;</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              The AI Business <span className="text-primary">Operating System</span>
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg">
              Seven integrated systems that form a complete AI-powered business platform. 
              Each one powerful alone — unstoppable together.
            </p>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-12"
          >
            <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-3xl font-heading font-bold text-primary">8+</p>
              <p className="text-sm text-gray-400">AI Employees</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-3xl font-heading font-bold text-cyan-400">7</p>
              <p className="text-sm text-gray-400">Platform Systems</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-3xl font-heading font-bold text-green-400">&infin;</p>
              <p className="text-sm text-gray-400">Tasks Automated</p>
            </div>
          </motion.div>

          {/* AI Workforce Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {[
              { title: 'AI Marketing Manager', desc: 'Plans campaigns, promotions, and brand strategy.', tags: ['Campaign planning', 'Brand strategy', 'Market analysis'], color: 'primary' },
              { title: 'AI Ads Manager', desc: 'Creates and manages paid advertising campaigns.', tags: ['Meta Ads', 'Google Ads', 'Budget optimization'], color: 'cyan' },
              { title: 'AI SEO Strategist', desc: 'Optimizes organic search traffic and rankings.', tags: ['Keyword research', 'On-page SEO', 'Content strategy'], color: 'green' },
              { title: 'AI Content Creator', desc: 'Writes blogs, ads, emails, and social content.', tags: ['Blog posts', 'Ad copy', 'Social media'], color: 'purple' },
              { title: 'AI Sales Assistant', desc: 'Qualifies leads and follows up with prospects.', tags: ['Lead qualification', 'Follow-ups', 'CRM updates'], color: 'yellow' },
              { title: 'AI Support Agent', desc: 'Handles customer inquiries and support tickets.', tags: ['Live chat', 'Ticket resolution', 'FAQ responses'], color: 'pink' },
              { title: 'AI Automation Engineer', desc: 'Builds workflows, automations, and integrations.', tags: ['Workflow design', 'API integrations', 'Process automation'], color: 'blue' },
              { title: 'AI Data Analyst', desc: 'Analyzes performance data and forecasts results.', tags: ['Performance reports', 'Forecasting', 'Insights'], color: 'amber' },
            ].map((employee, i) => (
              <motion.div
                key={employee.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-${employee.color}-500/30 hover:bg-${employee.color}-500/5 transition-all`}
              >
                <h3 className="text-base font-semibold text-white mb-2">{employee.title}</h3>
                <p className="text-sm text-gray-400 mb-3">{employee.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {employee.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-gray-400 border border-white/10">{tag}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* The Cost of Doing Nothing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 border border-red-500/20 mb-10"
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-4 text-center">
              The Cost of <span className="text-red-400">Doing Nothing</span>
            </h3>
            <p className="text-gray-400 text-center mb-6 max-w-2xl mx-auto">
              While you're manually posting, chasing leads, and paying for tools that don't talk to each other — your competitors are automating everything.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20">
                <h4 className="text-red-400 font-semibold mb-3">The Old Way</h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">$8,000–$15,000/month in staff salaries for tasks AI can do</p>
                  <p className="text-gray-300">6–12 disconnected tools with no single source of truth</p>
                  <p className="text-gray-300">Leads slipping through the cracks while you sleep</p>
                  <p className="text-gray-300">Hours wasted on manual work instead of growing</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20">
                <h4 className="text-green-400 font-semibold mb-3">With AI Horse-Powered Systems</h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">AI-powered campaigns launching in minutes</p>
                  <p className="text-gray-300">Leads followed up automatically — 24/7, no days off</p>
                  <p className="text-gray-300">Save $10K+/month by replacing manual work with AI</p>
                  <p className="text-gray-300">Data-driven insights while your competition is guessing</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Platform Systems - The 7 Solutions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h3 className="font-heading text-xl font-bold text-white mb-6 text-center">
              7 Integrated Platform Systems
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { name: 'AI Horse-Powered Workspace\u2122', desc: 'Slack-style collaboration hub for your team and AI', color: 'primary' },
                { name: 'AGI Workhorse\u2122', desc: 'AI task execution & multi-agent orchestration', color: 'yellow' },
                { name: 'AI Horse-Powered CRM\u2122', desc: 'AI-powered lead scoring & pipeline management', color: 'cyan' },
                { name: 'AI Horse-Powered POD\u2122', desc: 'Project operations, deliverables & team workflows', color: 'purple' },
                { name: 'AI Horse-Powered Directory\u2122', desc: 'Marketplace visibility & multi-platform listings', color: 'green' },
                { name: 'AI Horse-Powered Ad Manager\u2122', desc: 'Campaign creation, budget optimization & ROI', color: 'blue' },
                { name: 'AI Horse-Powered Bot\u2122', desc: 'Natural language commands & multi-agent routing', color: 'pink' },
              ].map((sys) => (
                <div key={sys.name} className={`flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-${sys.color}-500/30 transition-all`}>
                  <CheckCircle className={`w-4 h-4 text-${sys.color}-400 flex-shrink-0`} />
                  <div>
                    <p className="text-sm text-white font-medium">{sys.name}</p>
                    <p className="text-[11px] text-gray-500">{sys.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <div className="text-center">
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-10 py-5" data-testid="workspace-cta-btn">
                Launch Your AI Horse-Powered Systems&#8482; <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <p className="text-sm text-gray-500 mt-3">30-day free trial — No credit card required</p>
          </div>
        </div>
      </section>

      {/* AI Business Hero Directory Section */}
      <section className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-black via-purple-950/20 to-black">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-transparent to-cyan-900/10" />
        <FloatingParticles count={15} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Video Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="rounded-2xl overflow-hidden border-2 border-purple-500/30 shadow-2xl shadow-purple-500/20 max-w-4xl mx-auto bg-black">
              <video 
                src="https://customer-assets.emergentagent.com/job_f93b3d7c-705d-46e4-8005-5e3a42794b33/artifacts/lo88n29h_kling_20260205_Image_to_Video_Epic_cinem_1596_0.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full"
                style={{ display: 'block' }}
              />
            </div>
          </motion.div>

          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-6">
              <Globe className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">AI Business Hero Listing</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Become the <span className="text-purple-400">Hero</span> of Your Industry
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg">
              Get discovered by customers actively searching for your services. Our AI-powered directory puts your business 
              front and center with intelligent matching, reviews, and 24/7 visibility.
            </p>
          </motion.div>

          {/* How It Works Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-purple-500/20 text-center"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Claim Your Listing</h3>
              <p className="text-gray-400 text-sm">
                Create your business profile with AI-powered optimization. Stand out with rich media, services, and instant booking capabilities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 border border-cyan-500/20 text-center"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">AI-Powered Matching</h3>
              <p className="text-gray-400 text-sm">
                Our intelligent search connects you with customers who need exactly what you offer. Natural language queries find perfect matches.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 border border-green-500/20 text-center"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Grow Your Business</h3>
              <p className="text-gray-400 text-sm">
                Get reviews, track analytics, receive leads directly to your CRM, and watch your business grow with zero extra effort.
              </p>
            </motion.div>
          </div>

          {/* Features List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 border border-white/10 mb-10"
          >
            <h3 className="font-heading text-2xl font-bold text-white text-center mb-6">
              What's Included in Your Hero Listing
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Globe, text: 'Premium directory placement', color: 'text-purple-400' },
                { icon: Video, text: 'Video showcase & gallery', color: 'text-cyan-400' },
                { icon: MessageCircle, text: 'Direct messaging from customers', color: 'text-green-400' },
                { icon: Star, text: 'Reviews & ratings system', color: 'text-yellow-400' },
                { icon: BarChart3, text: 'Analytics & lead tracking', color: 'text-blue-400' },
                { icon: Bot, text: 'AI chatbot integration', color: 'text-pink-400' },
                { icon: Calendar, text: 'Appointment booking', color: 'text-orange-400' },
                { icon: Award, text: '"Best of Best" certification', color: 'text-amber-400' },
                { icon: Share2, text: 'Social media integration', color: 'text-indigo-400' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                    <Icon className={`w-5 h-5 ${item.color} flex-shrink-0`} />
                    <span className="text-gray-300 text-sm">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-gray-400 mb-6">
              Every membership includes your <span className="text-purple-400 font-semibold">AI Business Hero Listing</span> — 
              no extra cost, instant visibility!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-purple-500 hover:bg-purple-600 text-white text-lg px-8 py-5">
                  <Globe className="w-5 h-5 mr-2" /> Explore the Directory
                </Button>
              </a>
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary text-lg px-8 py-5">
                  Get Your Hero Listing <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Social Proof Counter */}
      <section className="py-6 bg-gradient-to-b from-black/40 to-card/30">
        <SocialProofCounter />
      </section>

      {/* Cinematic Ad System - HERO FEATURE */}
      <section className="py-20 px-6 bg-gradient-to-b from-card/30 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/10 via-transparent to-purple-900/10" />
        <FloatingParticles count={20} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
              <Video className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-400 font-medium">AI Horse-Powered Cinematic System</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Your <span className="text-cyan-400">Cinematic Ads</span>. Everywhere.
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg">
              A complete Cinematic Video Ad with lead-capturing video, ad copy, landing page, and automated nurture sequences. 
              Leads flow into your CRM where AI agents, appointment setting, and automations convert them into customers — putting you everywhere at once.
            </p>
          </motion.div>

          {/* Flow Diagram */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-2 flex-wrap">
              {/* Video + Listing */}
              <div className="flex flex-col items-center p-4 rounded-xl bg-cyan-500/20 border-2 border-cyan-400 ring-4 ring-cyan-400/20">
                <Video className="w-8 h-8 text-cyan-400 mb-2" />
                <span className="text-sm font-semibold text-cyan-400 text-center">Cinematic Video Ad</span>
                <span className="text-xs text-cyan-400/70">START HERE</span>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-600 hidden md:block" />
              
              {/* Directory */}
              <div className="flex flex-col items-center p-4 rounded-xl glass border border-primary/50">
                <Globe className="w-7 h-7 text-primary mb-2" />
                <span className="text-sm font-medium text-white text-center">Best of Best Directory</span>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-600 hidden md:block" />
              
              {/* Publishing Platforms */}
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { icon: Share2, name: 'Meta', color: 'blue' },
                  { icon: Target, name: 'Google', color: 'red' },
                  { icon: Video, name: 'TikTok', color: 'pink' },
                  { icon: FileText, name: 'Blogs', color: 'green' },
                  { icon: Calendar, name: 'Email', color: 'purple' },
                ].map((platform, idx) => (
                  <div key={platform.name} className="flex flex-col items-center p-3 rounded-lg glass border border-white/10">
                    <platform.icon className={`w-5 h-5 text-${platform.color}-400 mb-1`} />
                    <span className="text-xs text-gray-300">{platform.name}</span>
                  </div>
                ))}
              </div>
              <ChevronRight className="w-6 h-6 text-gray-600 hidden md:block" />
              
              {/* More channels */}
              <div className="flex flex-col items-center p-3 rounded-lg glass border border-white/10">
                <Sparkles className="w-5 h-5 text-yellow-400 mb-1" />
                <span className="text-xs text-gray-300">TV, Radio, Billboards +</span>
              </div>
            </div>
          </motion.div>

          {/* Best of Best Content Types */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <div className="text-center mb-6">
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Best of Best Content Channels</h3>
              <p className="text-sm text-gray-400">Your content streams to these premium channels</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { name: 'TV Commercials', icon: Tv, color: 'amber' },
                { name: 'Radio Ads', icon: Radio, color: 'green' },
                { name: 'Digital Billboards', icon: Monitor, color: 'blue' },
                { name: 'Post Cards', icon: Mail, color: 'cyan' },
                { name: 'Video Ads', icon: Film, color: 'red' },
                { name: 'Image Ads', icon: Image, color: 'purple' },
              ].map((type) => (
                <div 
                  key={type.name} 
                  className={`flex items-center gap-2 px-4 py-2 rounded-full glass border border-${type.color}-500/30 bg-${type.color}-500/10`}
                >
                  <type.icon className={`w-4 h-4 text-${type.color}-400`} />
                  <span className="text-sm text-white">{type.name}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Feature Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { icon: Sparkles, title: 'Workspace Services', desc: 'Done-For-You content creation by our 40+ virtual departments', color: 'primary' },
              { icon: Globe, title: 'Best of Best Directory', desc: 'Your cinematic listing hub with local business endorsements', color: 'yellow' },
              { icon: Send, title: 'Multi-Channel Publish', desc: 'One click deploys to Meta, Google, TikTok, blogs, and more', color: 'cyan' },
              { icon: MessageCircle, title: 'CRM & Support', desc: 'Full customer journey tracked from ad click to closed deal', color: 'green' },
            ].map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all"
              >
                <feature.icon className={`w-10 h-10 text-${feature.color}-400 mb-4`} />
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-10 py-5">
                Start Your Cinematic Journey <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider className="-mt-1 text-card/30" />

      {/* $120k Co-Op Funds Lead Magnet Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-card/30 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/10 via-transparent to-primary/10" />
        <FloatingParticles count={15} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 mb-6">
              <Gift className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400 font-medium">Business Co-Op Funding</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Get <span className="text-green-400">$120,000</span> in Co-Op Funds
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Start your <strong className="text-green-400">30-day FREE trial</strong> today and get access to your approved $120,000 in Co-Op Funds 
              deposited directly into your account — ready to use on any DFY or DWY services!
            </p>
            <p className="text-gray-500 max-w-xl mx-auto text-sm mt-3">
              Co-Op Funds: 4% Funding Fee or 5% Easy Payment Plan.{' '}
              <a 
                href="https://aihorsepowered.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline"
              >
                Learn about Path to Funding
              </a>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* What Co-Op Covers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-green-500/20"
            >
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-3 text-center">100% Covered by Co-Op</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  Websites & Landing Pages
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  Funnels & Automations
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  AI Agents & Voice Bots
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  CRM Setup & Support
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  All Design & Copy Services
                </li>
              </ul>
            </motion.div>

            {/* Big Number - Free Trial Focus */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-8 border border-green-500/30 bg-gradient-to-br from-green-500/10 to-transparent flex flex-col items-center justify-center text-center"
            >
              <div className="px-4 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-semibold mb-3">
                30 DAYS FREE
              </div>
              <p className="text-5xl sm:text-6xl font-heading font-bold text-green-400 mb-1">
                $120k
              </p>
              <p className="text-lg text-white font-semibold mb-1">Co-Op Funds</p>
              <p className="text-xs text-gray-400 mb-4">No credit card required to start</p>
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="w-full">
                <Button className="w-full bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-3 text-lg" data-testid="coop-cta-btn">
                  Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>

            {/* How It Works */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 border border-primary/20"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-3 text-center">How It Works</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
                  <p className="text-gray-300">Start your 30-day free trial</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
                  <p className="text-gray-300">Explore all 40+ departments & AI tools</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">3</span>
                  <p className="text-gray-300">Apply for $120k Co-Op funding</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">4</span>
                  <p className="text-gray-300">Scale with Done-For-You services!</p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-sm text-gray-500"
          >
            30-day free trial on all plans. Co-Op Funds: 4% Funding Fee or 5% Easy Payment Plan.{' '}
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
              See full pricing details →
            </a>
          </motion.p>
        </div>
      </section>

      {/* What Are Co-Op Funds - Explainer Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-black to-card/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/5 via-transparent to-green-900/5" />
        <FloatingParticles count={10} />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 mb-6">
              <CreditCard className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400 font-medium">Understanding Your Benefits</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              What Are <span className="text-green-400">Co-Op Funds</span>?
            </h2>
          </motion.div>

          <div className="space-y-8">
            {/* Main Explanation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-8 border border-green-500/20"
            >
              <p className="text-lg text-gray-300 mb-6">
                Co-Op Funds are <span className="text-green-400 font-semibold">business growth credits</span> rewarded to you when you become a Channel Partner of AI Horse-Powered Technology.
              </p>
              <p className="text-gray-400 mb-6">
                They are issued upfront and are designed to help pay for the <strong className="text-white">real work</strong> your business needs — not just software.
              </p>
              <p className="text-gray-400 mb-6">
                Instead of paying full price out of pocket for marketing, systems, and execution, your Co-Op Funds are applied to:
              </p>
              <ul className="grid sm:grid-cols-2 gap-3 mb-6">
                {[
                  'Done-For-You (DFY) services from our 40+ Virtual Departments',
                  'AI Horse-Powered software & tools',
                  'Marketing assets and campaigns',
                  'Automation builds and workflows',
                  'CRM & BDC-X setup',
                  'AI agents and support systems',
                  'AI compliance and policy installs',
                  'Publishing, directory placement, and incentives'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-1" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                <p className="text-lg text-white font-semibold">
                  In simple terms: <span className="text-green-400">👉 Co-Op Funds reduce or eliminate the cost of getting work done.</span>
                </p>
              </div>
            </motion.div>

            {/* Why Co-Op Funds Exist */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-8 border border-white/10"
            >
              <h3 className="font-heading text-2xl font-bold text-white mb-4">Why Co-Op Funds Exist</h3>
              <p className="text-gray-400 mb-4">Most businesses struggle because:</p>
              <ul className="grid sm:grid-cols-2 gap-2 mb-6">
                {['Growth tools are expensive', 'Marketing is risky', 'Hiring teams is costly', 'Execution gets delayed'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-300">
                    <X className="w-4 h-4 text-red-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-300 mb-4">
                <span className="text-green-400 font-semibold">Co-Op Funds solve that problem</span> by giving you pre-approved spending power inside a system that already knows how to build and execute for businesses like yours.
              </p>
              <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                <p className="text-lg text-white">
                  You don't just get software. <span className="text-primary font-bold">You get a funded execution engine.</span>
                </p>
              </div>
            </motion.div>

            {/* How Co-Op Funds Are Used */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-8 border border-white/10"
            >
              <h3 className="font-heading text-2xl font-bold text-white mb-4">How Co-Op Funds Are Used</h3>
              <p className="text-gray-400 mb-6">When you place an order with the Workspace:</p>
              <div className="space-y-3 mb-6">
                {[
                  'You submit a request (website, ads, automation, etc.)',
                  'Our team reviews and sends you a proposal',
                  'Your Co-Op Funds are applied to the order value',
                  'You only pay the small service or third-party cost (if applicable)',
                  'The work begins'
                ].map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center justify-center flex-shrink-0 font-bold">{idx + 1}</span>
                    <p className="text-gray-300 pt-0.5">{step}</p>
                  </div>
                ))}
              </div>
              
              {/* Example Box */}
              <div className="p-6 rounded-xl bg-gradient-to-r from-green-500/10 to-primary/10 border border-green-500/30">
                <h4 className="font-semibold text-white mb-3">Example:</h4>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">A <span className="text-white font-semibold">$3,000 service order</span></p>
                  <p className="text-gray-300">→ Co-Op Funds cover most or all of the service</p>
                  <p className="text-gray-300">→ You pay only the small service fee</p>
                  <p className="text-gray-300">→ The remaining amount is deducted from your Co-Op balance</p>
                </div>
                <p className="text-green-400 font-semibold mt-4">You just saved thousands while still getting professional execution.</p>
              </div>
            </motion.div>

            {/* What Makes Co-Op Funds Different */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-8 border border-white/10"
            >
              <h3 className="font-heading text-2xl font-bold text-white mb-6">What Makes Co-Op Funds Different</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-green-400 font-semibold mb-3">Co-Op Funds ARE:</h4>
                  <ul className="space-y-2">
                    {[
                      'Issued upfront',
                      'Used for real execution, not just tools',
                      'Applied automatically when you place Workspace orders',
                      'Designed to grow your business, not create debt',
                      'Tracked transparently in your billing dashboard'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-red-400 font-semibold mb-3">Co-Op Funds are NOT:</h4>
                  <ul className="space-y-2">
                    {[
                      'A loan',
                      'A cash payout',
                      'Something you have to spend blindly',
                      'A coupon with hidden rules'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                        <X className="w-4 h-4 text-red-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="text-gray-400 mt-6 text-center">
                They are a <span className="text-white font-semibold">growth credit system</span> tied to your success inside the platform.
              </p>
            </motion.div>

            {/* The Big Benefit */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass rounded-2xl p-8 border border-green-500/30 bg-gradient-to-br from-green-500/10 to-transparent text-center"
            >
              <h3 className="font-heading text-2xl font-bold text-white mb-4">The Big Benefit for You</h3>
              <p className="text-gray-300 mb-6">With Co-Op Funds, you can:</p>
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {['Launch faster', 'Spend less out of pocket', 'Avoid hiring multiple vendors', 'Eliminate wasted trial-and-error', 'Scale with confidence'].map((item, idx) => (
                  <span key={idx} className="px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                    {item}
                  </span>
                ))}
              </div>
              <p className="text-gray-400 mb-4">
                You're no longer choosing between: "Do I pay for tools?" or "Do I pay for help?"
              </p>
              <p className="text-xl text-white font-semibold mb-6">
                You get both — backed by Co-Op support.
              </p>
              
              <div className="p-6 rounded-xl bg-green-500/20 border border-green-500/40">
                <h4 className="text-lg font-bold text-green-400 mb-2">In One Sentence:</h4>
                <p className="text-xl text-white font-semibold">
                  Co-Op Funds turn your membership into funded execution.
                </p>
                <p className="text-gray-300 mt-3">
                  Instead of just giving you access to technology, AI Horse-Powered Technology gives you the ability to use it at scale — <span className="text-green-400 font-semibold">without carrying the full cost yourself</span>.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wave Divider */}
      <WaveDivider flip className="-mb-1 text-card/30" />

      {/* Success Journey Section */}
      <section className="py-16 px-6 bg-card/30 relative overflow-hidden">
        <GridPattern className="opacity-50" />
        <FloatingParticles count={10} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            {/* AI Horse Running GIF */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="mb-6 flex justify-center"
            >
              <img 
                src="/images/ai-horse-running.gif" 
                alt="AI Horse Running"
                loading="lazy"
                decoding="async"
                className="w-64 h-auto object-contain"
                style={{
                  filter: 'drop-shadow(0 0 30px rgba(34, 211, 238, 0.3))',
                }}
              />
            </motion.div>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Rocket className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Your Path to Success</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Your <span className="text-primary">Success Journey</span> Starts Here
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              From signup to success — follow these steps and make your Workspace work for you every single day!
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SUCCESS_JOURNEY.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-primary font-medium">Step {item.step}</span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-400">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <p className="text-lg text-gray-300 mb-6">
              <strong className="text-white">Pro Tip:</strong> The first thing successful clients do every day? 
              <span className="text-primary font-semibold"> Log into their Workspace!</span>
            </p>
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-8 py-4" data-testid="journey-start-btn">
                Start Your Journey Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Trust Element 2: Platform Ratings Bar */}
      <section className="py-10 px-6 bg-black/60 border-y border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <p className="text-sm text-gray-400 uppercase tracking-wider">Rated 4.9/5 overall by our clients</p>
            <h3 className="text-2xl font-heading font-bold text-white mt-2">"Must Have Service"</h3>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-6">
            {PLATFORM_RATINGS.map((platform, i) => (
              <motion.div
                key={platform.platform}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 px-5 py-3 rounded-xl glass border border-white/10"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${platform.color} flex items-center justify-center`}>
                  <Star className="w-5 h-5 text-white fill-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">{platform.platform}</span>
                    <span className="text-yellow-400 font-bold">{platform.rating}/5</span>
                  </div>
                  <p className="text-xs text-gray-400">{platform.reviews.toLocaleString()} reviews</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Directory & Publishing Section */}
      <section className="py-16 px-6 relative overflow-hidden">
        <GradientOrb size="lg" position="top-right" className="opacity-30" />
        <FloatingParticles count={8} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
              <Globe className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-400 font-medium">Community Platform</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Your <span className="text-cyan-400">Business Directory</span> & Publishing Hub
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Get discovered in our AI-powered business directory and publish content to reach thousands of potential customers!
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Directory Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-8 border border-cyan-500/20 hover:border-cyan-500/40 transition-all group"
            >
              <div className="flex items-center gap-4 mb-6">
                <img src={DIRECTORY_LOGO} alt="Directory" className="w-16 h-16 rounded-xl" />
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white">Business Directory</h3>
                  <p className="text-cyan-400">Get Found by Local Customers</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6">
                List your business in our AI-powered directory. Get AI Certified with "Best of Best" badge, 
                receive reviews, and connect with customers through our TAP Dispatch network of home service professionals.
              </p>
              
              <ul className="space-y-3 mb-6">
                {[
                  'AI Certified "Best of Best" badge for verified businesses',
                  'TAP Dispatch - 20+ home service categories',
                  '40+ Workspace Department experts available',
                  'Private messaging with potential customers',
                  'Bookmark & follow favorite businesses'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                  Explore Directory <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>

            {/* Publishing Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-8 border border-primary/20 hover:border-primary/40 transition-all group"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Send className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white">Publishing Hub</h3>
                  <p className="text-primary">Publish Everywhere, All at Once</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6">
                Publish articles, events, job listings, coupons, and more across multiple platforms simultaneously. 
                Build your brand presence and engage with your audience through valuable content distributed everywhere.
              </p>
              
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[
                  { icon: FileText, name: 'Articles', color: 'from-purple-500 to-pink-500' },
                  { icon: Briefcase, name: 'Jobs', color: 'from-teal-500 to-cyan-500' },
                  { icon: Calendar, name: 'Events', color: 'from-indigo-500 to-violet-500' },
                  { icon: Tag, name: 'Coupons', color: 'from-pink-500 to-rose-500' },
                  { icon: Image, name: 'Photos', color: 'from-rose-500 to-pink-500' },
                  { icon: Video, name: 'Videos', color: 'from-red-500 to-orange-500' },
                  { icon: ShoppingBag, name: 'Products', color: 'from-emerald-500 to-green-500' },
                  { icon: MessageCircle, name: 'Discuss', color: 'from-blue-500 to-cyan-500' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="text-center">
                      <div className={`w-10 h-10 mx-auto rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-1`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-xs text-gray-400">{item.name}</span>
                    </div>
                  );
                })}
              </div>
              
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="w-full btn-primary">
                  Start Publishing <Send className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>

          {/* How it connects */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 glass rounded-2xl p-6 border border-white/10"
          >
            <h3 className="font-heading text-xl font-semibold text-white mb-4 text-center">
              How Directory + Publishing + Workspace Connect
            </h3>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center mb-3">
                  <span className="text-lg font-bold text-cyan-400">1</span>
                </div>
                <p className="text-sm text-gray-300">List your business in the <strong className="text-cyan-400">Directory</strong> to get discovered</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-3">
                  <span className="text-lg font-bold text-primary">2</span>
                </div>
                <p className="text-sm text-gray-300"><strong className="text-primary">Publish</strong> content to engage customers & build authority</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-accent/20 flex items-center justify-center mb-3">
                  <span className="text-lg font-bold text-accent">3</span>
                </div>
                <p className="text-sm text-gray-300">Use <strong className="text-accent">Workspace</strong> services to scale your business operations</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* NEW: Affiliate Network & Training Section */}
      <section className="py-16 px-6 relative overflow-hidden bg-gradient-to-b from-black to-card/30">
        <GradientOrb size="lg" position="bottom-left" className="opacity-20" />
        <FloatingParticles count={6} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 mb-6">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400 font-medium">New Platform Features</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              <span className="text-green-400">Earn</span> While You <span className="text-cyan-400">Learn</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Join our affiliate network to earn commissions and access comprehensive training to master the entire platform!
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Affiliate Network Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-green-500/20 hover:border-green-500/40 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Affiliate Program</h3>
              <p className="text-gray-400 text-sm mb-4">
                Earn commissions by promoting businesses in our directory. Get your unique affiliate link and start earning today!
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Earn $60 per qualified referral',
                  'Track clicks & conversions',
                  'Promote any business listing',
                  'Monthly payouts available'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-green-400 font-medium">💰 AI Besty Program: $120K Grant Referrals</p>
              </div>
            </motion.div>

            {/* Platform Training Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 border border-cyan-500/20 hover:border-cyan-500/40 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">My Training</h3>
              <p className="text-gray-400 text-sm mb-4">
                Comprehensive training with 16 modules covering every aspect of the platform. Learn at your own pace!
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Onboarding & AI Certification',
                  'DFY, DWY & DIY Tools',
                  'Publishing & Rankings',
                  'CRM & Affiliate Training'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-cyan-400 font-medium">📚 16 Modules • Progress Tracking • Completion Badges</p>
              </div>
            </motion.div>

            {/* Affiliate Marketplace Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center mb-4">
                <Share2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Affiliate Marketplace</h3>
              <p className="text-gray-400 text-sm mb-4">
                Browse all available affiliate programs from businesses in our directory. Join programs and start promoting!
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Browse all affiliate programs',
                  'One-click program joining',
                  'Ready-to-use promo materials',
                  'Real-time earnings dashboard'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle className="w-3 h-3 text-purple-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-purple-400 font-medium">🚀 Scripts for Text, Email, Social & More</p>
              </div>
            </motion.div>
          </div>

          {/* Bottom highlight - What's New */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 glass rounded-2xl p-6 border border-white/10"
          >
            <h3 className="font-heading text-lg font-semibold text-white mb-4 text-center">
              ✨ What's New in the Platform
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Users, title: 'Leads Dashboard', desc: 'Track all your directory leads', color: 'text-cyan-400' },
                { icon: FileText, title: 'Affiliate Scripts', desc: 'Ready-to-use promotion templates', color: 'text-green-400' },
                { icon: Award, title: 'Training Center', desc: '16 modules, full platform guide', color: 'text-purple-400' },
                { icon: Gift, title: 'Resort Incentives', desc: 'Send free vacation certificates', color: 'text-pink-400' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all">
                    <Icon className={`w-5 h-5 ${item.color} flex-shrink-0 mt-0.5`} />
                    <div>
                      <p className="text-sm font-medium text-white">{item.title}</p>
                      <p className="text-xs text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gift Portal Section - Vacation Incentives */}
      <section className="py-16 px-6 relative overflow-hidden bg-gradient-to-b from-card/30 to-black">
        <GradientOrb size="lg" position="top-right" className="opacity-20" />
        <FloatingParticles count={6} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
              <Gift className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">Incentive Marketing Platform</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              <span className="text-amber-400">Gift Portal</span> - Turn Buyers Into Loyal Fans
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Instantly Boost Sales by 60% or More with Incentive Based Marketing. Empowering business owners to thrive!
            </p>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-12">
            {[
              { value: '20,000+', label: 'AI Horse-Powered Members', color: 'text-amber-400' },
              { value: '500,000+', label: 'Vacation Certificates Gifted', color: 'text-green-400' },
              { value: '$375M+', label: 'of Vacations Gifted', color: 'text-cyan-400' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass rounded-xl p-6 text-center border border-white/10"
              >
                <p className={`text-3xl md:text-4xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-gray-400 text-sm mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Resort Stays */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-amber-500/20 hover:border-amber-500/40 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4">
                <Plane className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Resort Stays</h3>
              <p className="text-gray-400 text-sm mb-4">
                Send unlimited resort vacation certificates to employees, clients & leads. Use as rewards, loyalty gifts, or irresistible lead magnets!
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Destinations worldwide',
                  '4-6 night resort stays',
                  'Unlimited certificates',
                  'Instant PDF delivery'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Hotel Savings Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 border border-cyan-500/20 hover:border-cyan-500/40 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Up to $500 Travel Cards</h3>
              <p className="text-gray-400 text-sm mb-4">
                Send digital hotel savings cards worth $100-$500 that recipients can use at over 1 million hotels worldwide!
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  '1M+ hotels worldwide',
                  '8,000+ car rental locations',
                  'No activation fee',
                  'Instant email delivery'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Custom Coupon Codes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 border border-green-500/20 hover:border-green-500/40 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4">
                <Tag className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Custom Coupon Codes</h3>
              <p className="text-gray-400 text-sm mb-4">
                We help create custom coupon codes for your own business to drive sales and reward loyal customers!
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Branded to your business',
                  'Track redemptions',
                  'Boost repeat purchases',
                  'Full setup assistance'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Testimonial style text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <p className="text-lg text-gray-300 italic max-w-3xl mx-auto">
              Join the <span className="text-amber-400 font-semibold">10,000+ business owners</span> who are enthusiastically 
              giving away vacation certificates... and <span className="text-green-400 font-semibold">dramatically growing their sales and incomes!</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marketing Flow Section - How It All Connects */}
      <section className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-[#0a0f1a] to-[#0d1525]">
        <GridPattern className="opacity-40" />
        <GradientOrb size="xl" position="center" className="opacity-20" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">AI Horse-Powered Marketing</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              How It All <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">Connects</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Never worry about where your next lead comes from again! Our integrated marketing ecosystem puts your business everywhere.
            </p>
          </motion.div>

          {/* Marketing Flow Diagram */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-8 border border-purple-500/20 mb-12"
          >
            {/* Central Hub */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <div className="text-center">
                    <Send className="w-10 h-10 text-white mx-auto mb-1" />
                    <span className="text-white text-sm font-bold">Publishing</span>
                  </div>
                </div>
                {/* Pulse animation */}
                <div className="absolute inset-0 rounded-full bg-purple-500/30 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
              <div className="mt-4 text-center">
                <p className="text-lg text-white font-semibold">Your Content Hub</p>
                <p className="text-sm text-gray-400">Create once, distribute everywhere</p>
              </div>
            </div>

            {/* Flow Lines & Channels */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: Globe, name: 'Directory', color: 'from-cyan-500 to-teal-500', desc: 'Local Discovery' },
                { icon: Target, name: 'Meta Ads', color: 'from-blue-500 to-indigo-500', desc: 'Facebook & Instagram' },
                { icon: FileText, name: 'Google Ads', color: 'from-red-500 to-orange-500', desc: 'Search & Display' },
                { icon: Award, name: 'SEO & Maps', color: 'from-green-500 to-emerald-500', desc: 'Organic Rankings' },
                { icon: MessageCircle, name: 'Email', color: 'from-amber-500 to-yellow-500', desc: 'Campaigns' },
                { icon: Users, name: 'TV & Radio', color: 'from-purple-500 to-violet-500', desc: 'Broadcast Media' },
              ].map((channel, i) => {
                const Icon = channel.icon;
                return (
                  <motion.div
                    key={channel.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="relative"
                  >
                    {/* Connection line */}
                    <div className="absolute -top-4 left-1/2 w-0.5 h-4 bg-gradient-to-b from-purple-500/50 to-transparent hidden sm:block" />
                    
                    <div className="glass rounded-xl p-4 border border-white/10 hover:border-purple-500/30 transition-all text-center group">
                      <div className={`w-12 h-12 mx-auto rounded-lg bg-gradient-to-br ${channel.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-white font-semibold text-sm">{channel.name}</p>
                      <p className="text-gray-500 text-xs mt-1">{channel.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Additional Channels Row */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-center text-gray-400 text-sm mb-6">Plus Traditional & Advanced Marketing Channels</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: FileText, name: 'Direct Mail', desc: 'Postcards & Letters' },
                  { icon: Image, name: 'Billboards', desc: 'Outdoor Advertising' },
                  { icon: Globe, name: 'Website Reveal', desc: 'Visitor Identification' },
                  { icon: Target, name: 'Buyer Intent', desc: 'Data & Signals' },
                ].map((channel, i) => {
                  const Icon = channel.icon;
                  return (
                    <motion.div
                      key={channel.name}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                      className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{channel.name}</p>
                        <p className="text-gray-500 text-xs">{channel.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* POD Services Gallery - 10 Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 mb-12"
          >
            <h3 className="text-center text-xl font-semibold text-white mb-6">Your AI Horse-Powered Team</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { src: '/images/virtual-team-pod.png', name: '40+ Virtual Departments' },
                { src: '/images/landing-cinematic-ads.png', name: 'Cinematic Ad System' },
                { src: '/images/landing-meta-ads.png', name: 'Meta Ads' },
                { src: '/images/landing-google-ads.png', name: 'Google Ads' },
                { src: '/images/landing-seo.png', name: 'SEO' },
                { src: '/images/landing-tiktok-ads.png', name: 'TikTok Ads' },
                { src: '/images/landing-directory.png', name: 'Best of Best Directory' },
                { src: '/images/landing-affiliate.png', name: 'Affiliate Network' },
                { src: '/images/landing-crm.png', name: 'AI Powered CRM' },
                { src: '/images/landing-coop-funding.png', name: '$120K Co-Op Funding' },
              ].map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all"
                >
                  <div className="aspect-square">
                    <img 
                      src={item.src}
                      alt={item.name}
                      className="w-full h-full object-contain bg-black/50 group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3">
                    <p className="text-white text-sm font-medium text-center">{item.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-block glass rounded-2xl p-8 border border-purple-500/30">
              <h3 className="font-heading text-2xl font-bold text-white mb-3">
                Stop Chasing Leads. Let Them Come to You.
              </h3>
              <p className="text-gray-400 mb-6 max-w-lg mx-auto">
                Our AI-powered marketing ecosystem ensures your business is visible across every channel — online and offline. One platform. Total coverage.
              </p>
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg px-8 py-4" data-testid="marketing-cta-btn">
                  Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider className="-mt-1 text-card/30" />
      
      {/* What is the Workspace Section */}
      <section className="py-14 px-6 bg-card/30 relative overflow-hidden">
        <GridPattern className="opacity-60" />
        <FloatingParticles count={12} />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col lg:flex-row items-center gap-10"
          >
            {/* Large Logo - 3D Transparent Style */}
            <div className="flex-shrink-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="logo-3d"
              >
                <img 
                  src="/images/ai-horse-logo.png" 
                  alt="AI Horse-Powered" 
                  className="w-64 h-64 object-contain drop-shadow-[0_20px_50px_rgba(34,211,238,0.4)] hover:drop-shadow-[0_25px_60px_rgba(34,211,238,0.5)] transition-all duration-500 hover:scale-105"
                  style={{
                    filter: 'drop-shadow(0 0 20px rgba(34, 211, 238, 0.3))',
                  }}
                />
              </motion.div>
            </div>
            
            {/* Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
                What is <span className="text-primary">AI Horse-Powered Systems</span>?
              </h2>
              <p className="text-xl text-gray-300 mb-3">
                <strong className="text-accent">The AI Business Operating System</strong> — Seven integrated systems that form a complete AI-powered business platform.
              </p>
              <p className="text-gray-400 max-w-xl">
                Deploy AI assistants, agents, and employees that run your marketing, operations, 
                and customer support. From your Workspace and CRM to the POD and Directory — 
                everything works together inside one powerful platform.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* What is AI Horse-Power Section */}
      <section className="py-14 px-6 relative overflow-hidden">
        <FloatingParticles count={12} />
        <GradientOrb size="lg" position="bottom-left" className="opacity-40" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col lg:flex-row-reverse items-center gap-10"
          >
            {/* AI Horse Image */}
            <div className="flex-shrink-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-cyan-500/20 blur-3xl rounded-full scale-105" />
                <img 
                  src="/images/ai-horse-power-hero.png"
                  alt="AI Horse-Powered"
                  className="relative z-10 w-80 h-80 object-contain hover:scale-105 transition-transform duration-500"
                  style={{
                    filter: 'drop-shadow(0 0 25px rgba(34, 211, 238, 0.4))',
                  }}
                />
              </motion.div>
            </div>
            
            {/* Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
                What is <span className="text-cyan-400">AI Horse-Power</span>?
              </h2>
              <p className="text-xl text-gray-300 mb-3">
                <strong className="text-primary">Technology & Support</strong> — Galloping into the future of business automation.
              </p>
              <p className="text-gray-400 max-w-xl mb-4">
                AI Horse-Power represents the fusion of artificial intelligence with unstoppable momentum. 
                Our colors — cyan, blue, and electric highlights — symbolize innovation, trust, and the energy 
                we bring to every project.
              </p>
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                  <div className="w-4 h-4 rounded-full bg-cyan-400" />
                  <span className="text-sm text-cyan-400">Innovation</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <div className="w-4 h-4 rounded-full bg-blue-400" />
                  <span className="text-sm text-blue-400">Trust</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30">
                  <div className="w-4 h-4 rounded-full bg-primary" />
                  <span className="text-sm text-primary">Energy</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider flip className="-mb-1" />
      
      {/* How It Works */}
      <section id="how-it-works" className="py-14 px-6 relative overflow-hidden">
        <GradientOrb size="lg" position="top-left" className="opacity-60" />
        <FloatingParticles count={10} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Accent Line */}
          <GlowLine className="mb-10" />
          
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
              Three Ways to Get It Done
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose your preferred approach — we adapt to how you work best.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-marketing card-glow group"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mb-5 group-hover:bg-primary/30 transition-colors">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider className="-mt-1 text-card/50" />
      
      {/* Process Steps */}
      <section className="py-14 px-6 bg-card/50 relative overflow-hidden">
        <GridPattern className="opacity-50" />
        <GradientOrb size="md" position="bottom-right" className="opacity-40" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
              From Brief to Complete in 4 Steps
            </h2>
            <GlowLine className="mt-4" />
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative h-full"
              >
                <div className="glass rounded-2xl p-5 h-full flex flex-col card-glow border border-primary/10">
                  <div className="text-5xl font-heading font-bold text-primary/30 mb-3">
                    {step.step}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-400 flex-grow">{step.description}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-primary/50">
                    <ChevronRight className="w-6 h-6" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider flip className="-mb-1 text-card/50" />

      {/* Trust Element 3: Full Testimonials Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-card/50 to-black relative overflow-hidden">
        <GridPattern className="opacity-30" />
        <FloatingParticles count={10} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/30 mb-6">
              <Quote className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400 font-medium">Client Success Stories</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              See What Our <span className="text-yellow-400">Clients</span> Are Saying
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Don't just take our word for it — hear from businesses that have transformed their operations with AI Horse-Powered.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {TESTIMONIALS.map((testimonial, index) => (
              <TestimonialCard key={testimonial.name} testimonial={testimonial} index={index} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-6 flex-wrap justify-center glass rounded-2xl px-8 py-4 border border-white/10">
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-primary">20,000+</p>
                <p className="text-sm text-gray-400">Happy Clients</p>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block" />
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-green-400">$50M+</p>
                <p className="text-sm text-gray-400">Client Revenue Generated</p>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block" />
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-yellow-400">4.9/5</p>
                <p className="text-sm text-gray-400">Average Rating</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Departments Preview */}
      <section id="departments" className="py-14 px-6 relative overflow-hidden">
        <FloatingParticles count={15} />
        <GradientOrb size="lg" position="top-right" className="opacity-40" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Workspace Departments Visual Banner with Parallax & Glow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10 relative rounded-2xl overflow-hidden group"
          >
            {/* Animated glow border */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-purple-500 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-60 blur-sm transition-opacity duration-700 animate-pulse" style={{ animationDuration: '3s' }} />
            
            {/* Image container with parallax */}
            <motion.div
              className="relative rounded-2xl overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <motion.img 
                src="/images/virtual-team-pod.png"
                alt="40+ Workspace Departments Network"
                className="w-full h-56 sm:h-72 object-cover rounded-2xl transform transition-transform duration-700 group-hover:scale-110"
              />
              
              {/* Animated scan line effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ animation: 'scanline 2s ease-in-out infinite' }} />
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent rounded-2xl" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-center">
                <motion.h2 
                  className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3"
                  animate={{ 
                    textShadow: ['0 0 20px rgba(0,255,255,0)', '0 0 30px rgba(0,255,255,0.5)', '0 0 20px rgba(0,255,255,0)']
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  40+ Virtual Departments
                </motion.h2>
                <p className="text-gray-300 max-w-2xl mx-auto">
                  From websites to AI agents, paid ads to automations — we&apos;ve got every department you need.
                  All departments are <span className="text-accent font-semibold">Co-Op eligible</span>.
                </p>
              </div>
            </motion.div>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {DEPARTMENTS_PREVIEW.map((dept, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="glass rounded-xl p-6 hover:border-primary/30 transition-colors cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <LogoIcon size="md" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{dept.name}</h3>
                      <p className="text-sm text-gray-400">{dept.count}+ services</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-primary transition-colors" />
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-secondary" data-testid="view-all-departments-btn">
                View All 40+ Departments <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider className="-mt-1 text-card/30" />

      {/* Become AI Certified Section */}
      <section className="py-16 px-6 bg-card/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/10 via-transparent to-cyan-900/10" />
        <FloatingParticles count={10} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
              <Award className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">AI Certification Program</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Become <span className="text-amber-400">AI Certified</span>, Secure & Compliant
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg">
              Get instant internal and customer-facing AI policies that make your business a 
              <span className="text-amber-400 font-medium"> "Voted Best of Best"</span> AI Certified business in your marketplace category.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Internal Policy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 border border-white/10 hover:border-amber-500/30 transition-colors text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Internal AI Policy</h3>
              <p className="text-gray-400 text-sm">
                Comprehensive guidelines for your team on AI usage, data handling, and responsible implementation.
              </p>
            </motion.div>

            {/* External Policy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Customer AI Disclosure</h3>
              <p className="text-gray-400 text-sm">
                Transparent policies for your customers about how AI enhances their experience with your business.
              </p>
            </motion.div>

            {/* Certification Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-transparent text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-amber-500/30 to-cyan-500/30 flex items-center justify-center mx-auto mb-4">
                <Award className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">"Best of Best" Badge</h3>
              <p className="text-gray-400 text-sm">
                Qualify for press releases, industry listings, and the AI Certified Business trust badge for your website.
              </p>
            </motion.div>
          </div>

          {/* Benefits List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 border border-amber-500/20 max-w-3xl mx-auto"
          >
            <h3 className="font-heading text-xl font-semibold text-white mb-6 text-center">
              What You Get Immediately
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">Customized internal AI use policy with your business details</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">Customer-facing AI disclosure ready for your website</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">PDF export for both policies to print or share</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">Eligibility for "Voted Best of Best" recognition</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">AI Certified trust badge for your marketing</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm">Press release and directory listing opportunities</p>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold px-8 py-3" data-testid="ai-certified-cta-btn">
                  Get AI Certified Now <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider flip className="-mb-1 text-card/30" />
      
      {/* AI Automated Onboarding Section */}
      <section className="py-14 px-6 bg-card/30 relative overflow-hidden">
        <GridPattern className="opacity-60" />
        <FloatingParticles count={12} />
        <GradientOrb size="lg" position="bottom-left" className="opacity-50" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">New Feature</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              AI Automated Onboarding <span className="text-primary">for Your CRM</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Get your brand set up in minutes, not days. Our intelligent Brand Breakdown captures everything 
              we need to deliver personalized, on-brand work from day one.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left: Brand Breakdown Steps */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="glass rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">Core Identity</h3>
                    <p className="text-gray-400 text-sm">Business name, contact details, industry vertical, service areas — the foundation of your brand.</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-secondary">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">Brand Voice</h3>
                    <p className="text-gray-400 text-sm">Tone, value proposition, elevator pitch — so every piece of content sounds authentically you.</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-accent">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">Compliance</h3>
                    <p className="text-gray-400 text-sm">Privacy policies, required disclosures — we keep your marketing legally sound.</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 border border-amber-500/20 hover:border-amber-500/40 transition-colors bg-gradient-to-r from-amber-500/5 to-transparent">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">AI Certification</h3>
                    <p className="text-gray-400 text-sm">Get certified as a "Voted Best of Best" AI business with internal & external policies.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Features & Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-8 border border-primary/20"
            >
              <div className="flex items-center gap-3 mb-6">
                <ClipboardList className="w-8 h-8 text-primary" />
                <h3 className="font-heading text-xl font-semibold text-white">Guided Brand Breakdown</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Auto-Save Progress</p>
                    <p className="text-gray-400 text-sm">Never lose your work — changes save automatically as you type.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Smart Timeline Checklist</p>
                    <p className="text-gray-400 text-sm">Track onboarding tasks with milestones that unlock as you progress.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">AI Certified Badge</p>
                    <p className="text-gray-400 text-sm">Get instant internal & external AI policies and qualify for "Best of Best" recognition.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Export to PDF</p>
                    <p className="text-gray-400 text-sm">Download your complete brand profile and AI policies anytime.</p>
                  </div>
                </div>
              </div>

              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary w-full" data-testid="onboarding-cta-btn">
                  Start Your Brand Breakdown <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider flip className="-mb-1 text-card/30" />
      
      {/* Meet Athena - AI Executive Assistant Section */}
      <section className="py-16 px-6 relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/10 via-transparent to-cyan-900/10" />
        <FloatingParticles count={15} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">Meet Your AI Executive Assistant</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Introducing <span className="text-amber-400">Athena</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Your personal AI goddess of strategy and wisdom, available 24/7 to guide you through every project.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Athena Visual */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="block relative mx-auto max-w-md cursor-pointer group">
                {/* Cyan/Blue glow behind image - matching logo style */}
                <div className="absolute inset-0 bg-cyan-500/20 blur-3xl rounded-full scale-105 group-hover:bg-cyan-500/30 transition-all duration-500" />
                
                {/* Athena Widget Screenshot with subtle glow effect */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="relative z-10 group-hover:scale-[1.02] transition-transform duration-500"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aipowered-5/artifacts/gvqw58vu_Untitled%20design%20%2896%29.png"
                    alt="Athena AI Executive Assistant"
                    className="w-full h-auto object-contain rounded-2xl"
                    style={{
                      filter: 'drop-shadow(0 0 20px rgba(34, 211, 238, 0.3))',
                    }}
                  />
                </motion.div>
                
                {/* Floating badge */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="absolute -bottom-4 -right-4 glass rounded-2xl p-4 border border-cyan-500/30 bg-black/80"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Always Online</p>
                      <p className="text-gray-400 text-xs">Text & Voice Ready</p>
                    </div>
                  </div>
                </motion.div>
              </a>
            </motion.div>

            {/* Right: Features */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="glass rounded-2xl p-6 border border-white/10 hover:border-amber-500/30 transition-colors group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/30 transition-colors">
                    <MessageCircle className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">Text & Voice Conversations</h3>
                    <p className="text-gray-400 text-sm">Chat with Athena anytime via text, or talk naturally using advanced AI voice technology. Get instant answers and guidance your way.</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-colors group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/30 transition-colors">
                    <Target className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">Strategic Project Guidance</h3>
                    <p className="text-gray-400 text-sm">Athena analyzes your goals and recommends the perfect services, departments, and approach for your projects.</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 border border-white/10 hover:border-purple-500/30 transition-colors group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/30 transition-colors">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-1">Personalized Updates</h3>
                    <p className="text-gray-400 text-sm">Athena greets you by name and proactively shares updates on your orders, tasks, and important notifications.</p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-3 w-full px-6 py-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold text-lg shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all"
                  data-testid="meet-athena-cta-btn"
                >
                  <span>Meet Athena Today</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Wave Divider */}
      <WaveDivider className="-mt-1 text-black" />
      
      {/* CTA Section */}
      <section className="py-14 px-6 relative overflow-hidden">
        <GradientOrb size="xl" position="center" className="opacity-40" />
        <FloatingParticles count={12} />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-10 border border-primary/20"
          >
            <div className="mb-5">
              <img 
                src="/images/ai-horse-logo.png" 
                alt="AI Horse-Powered" 
                className="w-20 h-20 mx-auto object-contain drop-shadow-[0_10px_30px_rgba(34,211,238,0.4)]"
              />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
              Ready to Start Your First Project?
            </h2>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Join businesses using AI Horse-Powered Systems to get more done with less effort.
            </p>
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-10 py-5" data-testid="cta-get-started-btn">
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Trust Element 4: Final Trust Badges */}
      <section className="py-8 px-6 bg-black/80 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">256-bit SSL Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-primary" />
              <span className="text-sm text-gray-300">AI Certified Platform</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-gray-300">Best of Best 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              <span className="text-sm text-gray-300">20,000+ Businesses</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-card/30 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-cyan-900/5" />
        <FloatingParticles count={8} />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <MessageCircle className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Got Questions?</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              AI Horse-Powered Systems&#8482; — <span className="text-primary">FAQ</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "What is AI Horse-Powered Systems?",
                a: "AI Horse-Powered Systems is the AI Business Operating System — seven integrated platforms (Workspace, AGI Workhorse, CRM, POD, Directory, Ad Manager, and Bot) that deploy AI assistants, agents, and employees to run your marketing, operations, and customer support. It bundles powerful execution with AI-driven support, automation, publishing, and performance tools into one connected platform."
              },
              {
                q: "How does the platform work?",
                a: "Once you place an order through the POD or Workspace, our team breaks down your request, scopes the work, and prepares a tailored proposal. You approve it, and our 40+ DFY (Done-For-You) departments, DWY sessions, and AI tools begin executing. The more you use, the more value you unlock from your Co-Op Funds and platform features."
              },
              {
                q: "What are Co-Op Funds?",
                a: "Co-Op Funds are business growth credits given to you when you join the platform. They're used to pay for DFY services, AI tools, automation builds, publishing support, CRM enhancements, and more — helping you grow with less out-of-pocket cost. You can apply them directly toward the work you need executed by our POD and Workspace teams."
              },
              {
                q: "What services are included?",
                a: "Your platform gives you access to: 40+ DFY departments covering marketing, automation, design, analytics, media, and more • AI tools for content creation, ads, automation, copywriting, and visuals • Support workflows and AI Concierge support • CRM + BDC-X service center for lead management and automation • POD project operations and team workflows • Publishing systems, Best of Best Directory placement, and lead generation frameworks. This means you get 'results-oriented execution,' not just software tools."
              },
              {
                q: "What is the AI Concierge?",
                a: "The AI Concierge is an intelligent assistant trained on your knowledge base, dashboard features, and platform processes. It can help you navigate the platform, explain features, place orders, answer questions about services, and guide you through next best steps. Think of it as a virtual team member ready to help anytime."
              },
              {
                q: "Can I use my Co-Op Funds for all services?",
                a: "Co-Op Funds cover most execution services and platform features such as DFY team work, AI tools, automations, and publishing support. Some third-party spend (like ad spend to Meta, Google, postcard printing + postage, or lead lists) may be billed at cost with a standard agency fee applied where applicable."
              },
              {
                q: "How do I place an order?",
                a: "It's simple: Go to New Order in your dashboard → Choose the service or department you need → Fill in the guided intake form (AI assistant helps) → Submit your request → Receive a tailored proposal → Approve to start execution. Everything is tracked in your Orders page with real-time status updates."
              },
              {
                q: "How do the AI Tools fit into my business?",
                a: "AI Tools unlock capabilities like: content generation (copy, visuals, scripts), AI-assisted advertising, workflow automation, creative assets, and voice/audio creation. Use them directly yourself (DIY) or as part of a DFY or DWY order. They're designed to speed up execution without needing outside hires."
              },
              {
                q: "What is the Best of Best Directory & Publishing?",
                a: "The Best of Best Directory is a curated marketplace where qualified businesses are featured for enhanced visibility. It's a publishing and exposure system that boosts discovery, credibility, and lead generation across platforms."
              },
              {
                q: "Is training or support included?",
                a: "Yes. You have access to: Onboarding guides, Knowledge base + searchable help, AI support & concierge, DWY live sessions, and Team messaging and help tickets. Assistance is part of the platform, not an extra add-on."
              },
              {
                q: "How does billing work with Co-Op Funds?",
                a: "Your billing dashboard shows: Co-Op Funds available, Services ordered, Service Fees (small percentage you pay), Any third-party costs, and Payment history. Co-Op is applied first, and you only pay what remains (usually service fees and pass-through costs)."
              },
              {
                q: "Can I upgrade or cancel at any time?",
                a: "Yes. Your plan is flexible. Upgrade or change your level of engagement based on your business needs. Co-Op Funds and execution capacity grow as you use the system and qualify for future funding tiers."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass rounded-xl border border-white/10 overflow-hidden"
              >
                <details className="group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="font-semibold text-white pr-4">{faq.q}</h3>
                    <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10 p-6 glass rounded-xl border border-primary/20"
          >
            <p className="text-gray-400 mb-3">📌 Need More Help?</p>
            <p className="text-white">
              If your question isn't listed here, our <span className="text-primary font-semibold">AI Concierge</span> and Support Team are ready to help right from your dashboard.
            </p>
            <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary mt-4">
                Get Started Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* Wave Divider before footer */}
      <WaveDivider className="-mt-1" />
      
      {/* Footer */}
      <footer id="support" className="py-12 px-6 bg-card/30 relative">
        <GridPattern className="opacity-40" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-3 gap-10">
            <div className="md:col-span-1">
              <Logo size="md" showText={true} className="mb-4" />
              <p className="text-gray-400 mb-4 max-w-sm">
                Your AI Business Operating System. Seven integrated platforms powering your marketing, operations, and customer support.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <div className="space-y-2">
                <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white transition-colors">Get Started</a>
                <a href="#departments" className="block text-gray-400 hover:text-white transition-colors">Departments</a>
                <a href="#how-it-works" className="block text-gray-400 hover:text-white transition-colors">How It Works</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <div className="space-y-2">
                <Link to="/login" className="block text-gray-400 hover:text-white transition-colors">Enter Portal</Link>
                <a href={EXTERNAL_PRICING_URL} target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white transition-colors">Create Account</a>
                <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white transition-colors">Business Directory</a>
                <a href={`${EXTERNAL_DIRECTORY_URL}/deals`} target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white transition-colors">Deals & Coupons</a>
              </div>
            </div>
          </div>
          
          {/* Agency/Partner Notice */}
          <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10 text-center">
            <p className="text-sm text-gray-400">
              <span className="text-white font-medium">Agencies & SaaS Companies:</span> Pricing is per business sub-account. 
              <Link to="/partners" className="text-primary hover:text-primary/80 ml-1">
                Contact us
              </Link> to ask about partner programs and special channel partner pricing.
            </p>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} AI Horse-Powered Technology. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
